import type { CSSProperties } from 'react';
import { useCallback } from 'react';

import { FloatingPortal, useFloating } from '@floating-ui/react';
import classNames from 'classnames';

import List from '@/icon/List';
import X from '@/icon/X';

import { computeColor } from '../../utils/colors';
import { computeAlignment } from '../../utils/computeAlignment';

import type { NavbarMenuBurgerElementProps } from '../../navbarElement/Navbar.type';
import * as styles from '../Navbar.css';
import { NAVBAR_MENU_BURGER_DEFAULTS_V2 } from './NavbarMenuBurger.constants';
import * as v2Styles from './NavbarMenuBurger.css';

export function NavbarMenuBurgerV2(props: NavbarMenuBurgerElementProps) {
  const { element, className, attributes = {}, children, onClick, isInEditor, isLive, isFixed } = props;
  const {
    color,
    size,
    customAttributes,
    gap,
    contentPadding,
    contentBackgroundColor,
    contentBorder,
    contentBorderColor,
    contentBorderWidth,
    contentBorderStyle,
    contentBorderRadius,
    itemsAlignment,
  } = element.attrs || {};

  const { refs, floatingStyles, y } = useFloating({
    strategy: isLive === false || isFixed ? 'fixed' : 'absolute',
    placement: 'bottom-end',
    transform: false,
  });

  // Attach both reference and position reference
  const setButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      requestAnimationFrame(() => {
        refs.setReference(node);

        if (node) {
          const navbarAncestor = node.closest<HTMLElement>('.navbar-list');
          if (navbarAncestor) {
            // Ensure it can act as a containing block
            if (getComputedStyle(navbarAncestor).position === 'static') {
              navbarAncestor.style.position = 'relative';
            }
            refs.setPositionReference(navbarAncestor);
          }
        }
      });
    },
    [refs],
  );

  const RenderedDropdown = (
    <div
      ref={refs.setFloating}
      style={
        {
          // Floating popup related
          ...floatingStyles,
          top: `${y}px`,
          left: '0px',
          zIndex: 9999,
          // Styling related
          '--content-gap': gap || NAVBAR_MENU_BURGER_DEFAULTS_V2.gap,
          '--content-padding': contentPadding || NAVBAR_MENU_BURGER_DEFAULTS_V2.padding,
          '--content-background': contentBackgroundColor,
          '--content-border': contentBorder,
          '--content-border-color': contentBorderColor,
          '--content-border-width': contentBorderWidth,
          '--content-border-style': contentBorderStyle,
          '--content-border-radius': contentBorderRadius,
          '--content-items-alignment': computeAlignment(itemsAlignment),
        } as CSSProperties
      }
    >
      {children}
    </div>
  );

  return (
    <>
      <button
        ref={setButtonRef}
        type="button"
        onClick={onClick}
        {...customAttributes}
        {...attributes}
        className={classNames(styles.navbarMenuBurgerV2, className, customAttributes?.className)}
        style={
          {
            '--text-color': computeColor(color ?? NAVBAR_MENU_BURGER_DEFAULTS_V2.color),
          } as CSSProperties
        }
      >
        <List size={size || NAVBAR_MENU_BURGER_DEFAULTS_V2.size} weight="bold" className={v2Styles.openIconV2} />
        <X size={size || NAVBAR_MENU_BURGER_DEFAULTS_V2.size} weight="bold" className={v2Styles.closeIconV2} />
      </button>

      {isInEditor ? RenderedDropdown : <FloatingPortal>{RenderedDropdown}</FloatingPortal>}
    </>
  );
}
